import {
  AnchorButton,
  Button,
  ControlGroup,
  FormGroup,
  IButtonProps,
  IFormGroupProps,
  InputGroup,
  Intent,
  INumericInputProps,
  Tag,
  Tooltip,
  Position,
  ProgressBar,
  Icon,
  IInputGroupProps
} from '@blueprintjs/core'
import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import CX from 'classnames'
import { IconNames } from '@blueprintjs/icons'
import Client from '../../../../../client'
import RichEditor from '../../../../../components/editors/Rich'
import Help from '../../../../../components/Help'
import { LoadableSelect } from '../../../../../components/Inputs'
import { IndexedInventoryLocationSelect } from '../../../../../components/inputs/IndexedInventoryLocationSelect'
import NullSafeNumericInput from '../../../../../components/NullSafeNumericInput'
import WeightInput from '../../../../../printing/components/WeightInput'
import { isRequiredError, renderError, renderErrorExceptRequired, stylizeTitle } from '../Helpers'
import { useScaleStreamer } from '../../../../../printing/scale'
import { debounce, sanitizeOperationalSku, stripExtraSpace } from '../../../../../utils'
import { hexForIntent } from '../../../../../utils/color'
import { UprightStyle } from '../../../../../types'
import { GutterTooltipError } from '../GutterTooltipError'
import { ProductFormGroup } from '../ProductFormGroup'
import { showToast } from '../../../../../Toast'
import { MINIMUM_TRHRESHOLD_FOR_RELEVANT_PRICE } from '../PricePrediction/Helpers'

export type FormChangeHandler = (changeHash: { name: any; value: any }, callback?: () => void) => void
interface TitleInputProps {
  disabled?: boolean
  value?: string
  priceRecommendation?: any
  priceRecommendationQuery: any
  onChange: FormChangeHandler
  onLoading?: (loading: boolean) => void
  maxCharCount?: number
  error?: any
  errorTooltipOffset?: number
  showRequiredLabel?: boolean
}

export const TitleInput = React.memo<TitleInputProps & IFormGroupProps>(
  ({
    disabled,
    value,
    priceRecommendation,
    priceRecommendationQuery,
    onChange,
    maxCharCount = 80,
    error,
    showRequiredLabel,
    onLoading,
    ...props
  }) => {
    const titleStyles: UprightStyle = {
      searchMessage: { paddingLeft: '5px', paddingTop: '5px', display: 'inline-block', color: '#5C7080' }
    }
    const length = (value && value.length) || 0
    const invalid = length > maxCharCount
    const invalidPricePrediction = length > maxCharCount - 1

    const [noResultsProvided, setNoResultsProvided] = useState(1)
    const [loading, setLoading] = useState(false)
    const [lastCheckedValue, setLastCheckedValue] = useState<string>('')
    const [initialLoading, setInitialLoading] = useState(null)
    const [minimumThresholdRelevance, setMinimumThresholdRelevance] = useState(false)
    const [expandSearchTerms, setExpandSearchTerms] = useState(false)
    const [foundRelevance, setFoundRelevance] = useState(false)

    const pricePredictionEnabled = priceRecommendationQuery?.experiment?.beta

    useEffect(() => {
      if (!pricePredictionEnabled) return
      if (length === 0) {
        setInitialLoading(false)
      }
      if (length > 0 && initialLoading === null) {
        setInitialLoading(true)
        setLastCheckedValue(value)
      }

      if (priceRecommendation?.org)
        if (priceRecommendation.phase_2) {
          setInitialLoading(false)
          const surpassedMinimumThresholdForRelevantPrice =
            parseFloat(priceRecommendation.org[0]?.relevance) > MINIMUM_TRHRESHOLD_FOR_RELEVANT_PRICE
          setFoundRelevance(surpassedMinimumThresholdForRelevantPrice)
          setMinimumThresholdRelevance(!surpassedMinimumThresholdForRelevantPrice)
          setExpandSearchTerms(false)
        } else {
          setInitialLoading(false)
          setMinimumThresholdRelevance(invalidPricePrediction)
          setExpandSearchTerms(!invalidPricePrediction)
          setFoundRelevance(false)
        }
    }, [priceRecommendation, pricePredictionEnabled, initialLoading, invalidPricePrediction, length, value])

    const getPriceRecommendations = debounce(async (title: string) => {
      if (title === '') {
        return
      }
      try {
        setLoading(true)
        let experiment = []

        if (priceRecommendationQuery?.experiment?.beta) {
          if (priceRecommendationQuery?.experiment?.sharing) experiment = ['org', 'global']
          else experiment = ['org']
        }

        for (const exp of experiment) {
          const data = await Client.v5ProductsPriceRecommendations({
            experiment: exp,
            pricePrediction: {
              title,
              category: priceRecommendationQuery.category,
              compositeSearchTerms: priceRecommendationQuery.compositeSearchTerms
            }
          })

          if (data.data) {
            onChange({ name: 'priceRecommendation', value: data.data })
            if (exp === 'org')
              if (data.data?.phase_2 && data.data?.org.length > 0) {
                if (data.data.org[0].relevance > MINIMUM_TRHRESHOLD_FOR_RELEVANT_PRICE) {
                  onChange({ name: 'resultsProvided', value: true })
                } else {
                  setNoResultsProvided(noResultsProvided + 1)
                  onChange({ name: 'noResultsProvided', value: noResultsProvided })
                }
              } else {
                setNoResultsProvided(noResultsProvided + 1)
                onChange({ name: 'noResultsProvided', value: noResultsProvided })
              }
          }
        }
      } catch (_e) {
        // fail silently - this is a small value-add, not critical to workflow
        // eslint-disable-next-line no-console
        console.log(_e)
      } finally {
        setLastCheckedValue(title)
        // we want to inject ~3 second delay to show the User "generating price prediction matches from title", this ensure we will minimally have 1.5 seconds
        setTimeout(() => {
          setLoading(false)
        }, 1500)
      }
    }, 500)

    const checkTitleForPricePredictionsMemo = React.useCallback(
      (title: string) => {
        if (lastCheckedValue === title) return
        getPriceRecommendations(title)
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [priceRecommendationQuery, lastCheckedValue]
    )

    const changeHandler = v => {
      onChange({ name: 'title', value: v })
    }

    return (
      <>
        <ProductFormGroup title="Title" error={error} showRequiredLabel={showRequiredLabel} {...props}>
          <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
            <InputGroup
              type="text"
              disabled={disabled}
              value={value}
              intent={error || invalid ? Intent.DANGER : Intent.NONE}
              placeholder="New Nike Black Shoes"
              onChange={e => changeHandler(e.target.value)}
              onBlur={() => {
                // Users sometimes leave two spaces after editing titles.
                // But some places in our UI will only render 1 of the consecutive spaces, and it breaks searching for it.
                // So prevent titles from having multiple spaces in the first place (just like the builder template engine).
                if (value) {
                  if (pricePredictionEnabled) {
                    checkTitleForPricePredictionsMemo(stripExtraSpace(value))
                  }
                  onChange({ name: 'title', value: stripExtraSpace(value) })
                }
              }}
              rightElement={<Tag minimal>{`${length}/${maxCharCount}`}</Tag>}
            />
          </GutterTooltipError>
          {pricePredictionEnabled && (
            <div className="bp3FormHelperText">
              {!loading && foundRelevance && (
                <>
                  <Icon icon={IconNames.SERIES_SEARCH} color="#007EFA" />

                  <span style={titleStyles.searchMessage}>Price prediction matches found from title</span>
                </>
              )}
              {(loading || initialLoading) && (
                <>
                  <Button
                    disabled
                    loading={loading || initialLoading}
                    minimal
                    intent={Intent.PRIMARY}
                    icon="series-search"
                  />

                  <span style={titleStyles.searchMessage}>Generating price prediction matches from title</span>
                </>
              )}

              {!loading && expandSearchTerms && (
                <>
                  <Icon icon={IconNames.WARNING_SIGN} color="#FFA629" />
                  <span style={titleStyles.searchMessage}>
                    Increase title length and details to generate price prediction matches
                  </span>
                </>
              )}
              {!loading && minimumThresholdRelevance && (
                <>
                  <span style={titleStyles.searchMessage}>
                    No historical listing matches found, potentially rare item
                  </span>
                </>
              )}
            </div>
          )}
        </ProductFormGroup>
      </>
    )
  }
)

interface SwapInputProps {
  value?: string
  name?: React.ReactNode
  onSwap: (event: React.MouseEvent<HTMLElement>) => void
}

export const SwapInput = React.memo<SwapInputProps & IFormGroupProps>(({ value, name, onSwap, ...props }) => (
  <FormGroup label={name} {...props}>
    <ControlGroup fill>
      <InputGroup type="text" disabled value={value} className="bp3-fill" />
      <Tooltip content="Click to change">
        <AnchorButton intent="primary" icon="swap-horizontal" onClick={onSwap} />
      </Tooltip>
    </ControlGroup>
  </FormGroup>
))

interface SKUInputProps {
  inputProps?: Partial<IInputGroupProps>
  buttonProps?: Partial<IButtonProps>
  value?: string
  placeholder?: string
  onChange: FormChangeHandler
  error?: any
  supplierId?: number
  isActiveProduct?: boolean
  errorTooltipOffset?: number
  showRequiredLabel?: boolean
}

const skuInputStyles: UprightStyle = {
  warningIntentColor: { color: hexForIntent(Intent.WARNING) },
  floatRight: { float: 'right' }
}

export const SKUInput = React.memo<SKUInputProps & IFormGroupProps>(
  ({ inputProps = {}, value, placeholder, onChange, buttonProps = {}, error, isActiveProduct, ...props }) => {
    const [loading, setLoading] = React.useState(null)

    const [isDuplicateSku, setIsDuplicateSku] = React.useState(false)

    const checkSku = debounce(async (sku: string) => {
      if (sku === '') {
        setIsDuplicateSku(false) // nice to clear warning if they clear the input
        return
      }
      if (isActiveProduct) {
        // We want this validation to run when editing a draft product or posting a product for the first time.
        // If they're editing an active product, skip this check (although honestly they shouldn't be changing the sku at that point).
        return
      }

      try {
        setLoading(true)
        const data = await Client.v5ProductsIndex({ sku, include_variants: true })

        if (data.data?.products?.length > 0) {
          setIsDuplicateSku(true)
          analytics.track('Product Form Duplicate Sku Detected')
        } else {
          setIsDuplicateSku(false)
        }
      } catch (_e) {
        // fail silently - this is a small value-add, not critical to workflow
        // eslint-disable-next-line no-console
        console.log(_e)
      } finally {
        setLoading(false)
      }
    }, 500)

    const checkSkuMemo = React.useCallback((sku: string) => {
      checkSku(sku)
      // Might be a correct way to memoize this, but inlining checkSku here will break the debouncing.
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const changeHandler = v => {
      onChange({ name: 'sku', value: v })
      checkSkuMemo(v)
    }

    const newSku = async () => {
      setLoading(true)
      try {
        const res = await Client.newSku(props.supplierId)
        onChange({ name: 'sku', value: res.data.sku })
      } catch (_e) {
        showToast('Could not generate SKU, please try again', Intent.WARNING, false)
      } finally {
        setLoading(false)
        analytics.track('Product Form Generate SKU Clicked')
      }
    }

    let intent
    if (error) {
      intent = 'danger'
    } else if (isDuplicateSku) {
      intent = 'warning'
    } else {
      intent = 'none'
    }

    const labelInfo = props.showRequiredLabel ? 'Required' : null

    let helperText
    if (isDuplicateSku) {
      // Note, the link this constructs is using the general "query" param. This also searches product titles.
      // But the checkSku API call above is using the "sku" param. So even though the checkSku logic is quite precise,
      // it's possible they may see some extra products when they view these results.
      // It's not worth building some URL that searches JUST by the sku.
      helperText = (
        <>
          Duplicate – this SKU already exists
          <Link style={skuInputStyles.floatRight} to={`/products?query=${value}`}>
            View matching products
          </Link>
        </>
      )
    } else {
      helperText = renderErrorExceptRequired(error, labelInfo)
    }

    let title
    if (isDuplicateSku) {
      title = <span style={skuInputStyles.warningIntentColor}>SKU</span>
    } else {
      title = stylizeTitle('SKU', error)
    }

    return (
      <FormGroup // ProductFormGroup is tricky to use here
        label={title}
        labelInfo={isRequiredError(error) ? renderError(labelInfo) : labelInfo}
        intent={intent}
        helperText={helperText}
        {...props}
        className={CX(props.className, 'float-right-formgroup-label')}
      >
        <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <InputGroup
            placeholder={placeholder || 'Barcode'}
            value={value || ''}
            onChange={e => changeHandler(sanitizeOperationalSku(e.target.value))}
            intent={intent}
            rightElement={
              <Button
                disabled={!!value}
                loading={loading}
                onClick={newSku}
                minimal
                intent={Intent.WARNING}
                icon="barcode"
                {...buttonProps}
              />
            }
            {...inputProps}
          />
        </GutterTooltipError>
      </FormGroup>
    )
  }
)

interface ShippingMethodSelectProps {
  onChange: FormChangeHandler
  error?: any
  showRequiredLabel?: boolean
  value?: string
  autoload?: boolean
  process?: (d: any) => any
  errorTooltipOffset?: number
}

const defaultShippingMethodOptionProcessor = d => d.carriers.map(s => ({ value: s.id, label: s.name }))
export const ShippingMethodSelect = React.memo<ShippingMethodSelectProps & IFormGroupProps>(
  ({ error, onChange, value, process, showRequiredLabel, ...props }) => {
    const processor = process || defaultShippingMethodOptionProcessor

    return (
      <ProductFormGroup title="Shipping Method" error={error} showRequiredLabel={showRequiredLabel} {...props}>
        <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <LoadableSelect
            load={Client.carriersIndex}
            process={processor}
            onChange={v => onChange({ name: 'carrierId', value: v })}
            value={value}
            style={{ marginTop: 0 }}
            autoload
          />
        </GutterTooltipError>
      </ProductFormGroup>
    )
  }
)

interface BaseSelectProps {
  error?: any
  onChange: FormChangeHandler
  value?: string | number
  autoload?: boolean
}

const defaultSupplierProcessor = d =>
  d.suppliers.map(s => ({
    value: s.id,
    label: s.allowedForStrategy === false ? `${s.name} (Not allowed for the selected strategy)` : s.name,
    disabled: s.allowedForStrategy === false
  }))
interface SupplierSelectProps extends BaseSelectProps {
  listingStrategyId?: number
  disabledReason?: string
  showRequiredLabel?: boolean
  errorTooltipOffset?: number
  hideLabel?: boolean
}
const fullWidthStyle = { style: { width: '100%', display: 'block' } }
/**
 * @deprecated Use SupplierSelectSWR instead
 */
export const SupplierSelect = React.memo<SupplierSelectProps & IFormGroupProps>(
  ({ error, onChange, value, listingStrategyId, disabledReason, showRequiredLabel, ...props }) => {
    const title = props.hideLabel ? '' : 'Supplier'
    return (
      <ProductFormGroup title={title} error={error} showRequiredLabel={showRequiredLabel} {...props}>
        <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
          <Tooltip content={disabledReason} disabled={!disabledReason} targetProps={fullWidthStyle}>
            <LoadableSelect
              load={() => Client.suppliersIndex({ listingStrategyId })}
              disabled={props.disabled || !!disabledReason}
              process={defaultSupplierProcessor}
              onChange={v => onChange({ name: 'supplierId', value: v })}
              style={{ marginTop: 0 }}
              value={value}
              autoload
            />
          </Tooltip>
        </GutterTooltipError>
      </ProductFormGroup>
    )
  }
)

export const InventoryLocationSelect = React.memo<
  BaseSelectProps &
    IFormGroupProps & { hideLabel?: boolean; showRequiredLabel?: boolean; title?: string; errorTooltipOffset?: number }
>(({ error, onChange, value, ...props }) => {
  const Selector = (
    <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.RIGHT}>
      <IndexedInventoryLocationSelect
        autoload={props.autoload}
        disabled={props.disabled}
        value={value}
        onChange={v => onChange({ name: 'inventoryLocationId', value: v })}
        skipIndex
      />
    </GutterTooltipError>
  )

  if (props.hideLabel) return Selector

  return (
    <ProductFormGroup
      error={error}
      showRequiredLabel={props.showRequiredLabel}
      {...props}
      title={props.title || 'Inventory Location'}
    >
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.RIGHT}>
        {Selector}
      </GutterTooltipError>
    </ProductFormGroup>
  )
})

const paddingInputStyle = {
  titleStyle: { marginRight: 8 },
  helpIconStyle: { marginTop: 0 }
}

interface PaddingInputProps {
  value?: string
  onChange: FormChangeHandler
  paddingChoices: any
  error?: any
}

export const PaddingInput = React.memo<PaddingInputProps & IFormGroupProps>(
  ({ value, onChange, error, paddingChoices, ...props }) => {
    return (
      <FormGroup
        intent={error ? 'danger' : 'none'}
        helperText={renderError(error)}
        label={
          <div>
            <span style={paddingInputStyle.titleStyle}>Box Padding</span>
            <Help extraIconStyle={paddingInputStyle.helpIconStyle} width="300px">
              <p>
                The amount of extra space a shipping box needs for packing material. This will affect shipping box
                calculation.
              </p>
            </Help>
          </div>
        }
        {...props}
      >
        <LoadableSelect
          load={() => paddingChoices}
          raw
          process={shippingBoxPaddingChoices =>
            shippingBoxPaddingChoices.map(choice => ({
              value: parseFloat(choice.value),
              label: choice.name
            }))
          }
          onChange={v => {
            onChange({ name: 'boxPadding', value: v })

            analytics.track(`Product Padding Input`, {
              value: v
            })
          }}
          value={value}
          autoload
        />
      </FormGroup>
    )
  }
)

interface ItemDimensionInputProps {
  title: string
  fieldName: string
  error?: any
  onChange: FormChangeHandler
  value?: string
}
export const ItemDimensionInput = React.memo<ItemDimensionInputProps & IFormGroupProps>(
  ({ title, fieldName, error, onChange, value, ...props }) => (
    <FormGroup label={stylizeTitle(title, error)} helperText={renderError(error)} {...props}>
      <NullSafeNumericInput
        preventNegative
        min={0}
        minorStepSize={0.01}
        fill
        buttonPosition="none"
        style={{ height: 36 }}
        onValueChange={(_, v) => onChange({ name: fieldName, value: v === '' ? null : v })}
        intent={error ? 'danger' : 'none'}
        value={value}
        rightElement={
          <Tag minimal style={{ marginTop: 8 }}>
            IN
          </Tag>
        }
      />
    </FormGroup>
  )
)

interface ItemWeightInputProps {
  error?: any
  showRequiredLabel?: boolean
  onChange: FormChangeHandler
  value: string
  className: string
  minorStepSize: number
  errorTooltipOffset?: number
}
export const ItemWeightInput = React.memo<ItemWeightInputProps>(({ error, onChange, value, className, ...props }) => {
  const [scaleName, scaleWeight, scaleConnected] = useScaleStreamer()
  return (
    <WeightInput
      formGroupClassName="stretch-half"
      intent={error ? 'danger' : 'none'}
      min={0}
      minorStepSize={0.001}
      selectAllOnFocus
      fill
      label={stylizeTitle('Item Weight', error)}
      value={value}
      onValueChange={(_, v) => {
        const assignShippingWeight = () => onChange({ name: 'shippingWeight', value: v })
        onChange({ name: 'weight', value: v }, () => assignShippingWeight())
      }}
      scaleName={scaleName}
      scaleWeight={scaleWeight}
      scaleConnected={scaleConnected}
      error={error}
      {...props}
    />
  )
})

interface ShippingWeightInputProps {
  error?: any
  showRequiredLabel?: boolean
  onChange: FormChangeHandler
  style?: React.CSSProperties
  carrierId?: number
  weight?: number
  shippingBoxId?: number
  errorTooltipOffset?: number
}
export const ShippingWeightInput = React.memo<ShippingWeightInputProps & INumericInputProps>(
  ({ error, onChange, style, showRequiredLabel, ...props }) => {
    const { errorTooltipOffset, shippingBoxId, carrierId, ...domFriendlyProps } = props

    return (
      <ProductFormGroup title="Shipping Weight" error={error} showRequiredLabel={showRequiredLabel} style={style}>
        <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.RIGHT}>
          <NullSafeNumericInput
            preventNegative
            min={0}
            minorStepSize={0.001}
            fill
            intent={error ? 'danger' : 'none'}
            onValueChange={(_, v) => onChange({ name: 'shippingWeight', value: v })}
            selectAllOnFocus
            style={{ height: 36 }}
            rightElement={
              <>
                <Tag minimal style={{ marginTop: 8 }}>
                  LBS
                </Tag>
              </>
            }
            {...domFriendlyProps}
          />
        </GutterTooltipError>
      </ProductFormGroup>
    )
  }
)

interface DescriptionEditorProps {
  error?: any
  value?: string
  onChange: FormChangeHandler
  errorTooltipOffset?: number
  showRequiredLabel?: boolean
}
export const DescriptionEditor = React.memo<DescriptionEditorProps & IFormGroupProps>(
  ({ error, value, onChange, showRequiredLabel, ...props }) => {
    const labelInfo = showRequiredLabel ? 'Required' : null

    return (
      <GutterTooltipError isOpen={error} offsetPx={props.errorTooltipOffset} position={Position.LEFT}>
        <FormGroup // ProductFormGroup is tricky to use here
          label={stylizeTitle('Description', error)}
          labelInfo={
            // There are basically two cases we'll see today:
            // - We need to show a gray "Required" text
            // - We need to show this ^ in red.
            // but in theory this should handle other cases like, if we started returning an error "can't contain the word ISIS"
            renderErrorExceptRequired(error, labelInfo) || (isRequiredError(error) ? renderError(labelInfo) : labelInfo)
          }
          {...props}
          className={CX(props.className, 'float-right-formgroup-label')}
          intent={error ? 'danger' : 'none'}
        >
          <RichEditor
            showTemplates
            value={value}
            onChange={v => onChange({ name: 'description', value: v })}
            style={{ position: 'relative', marginTop: 0 }}
          />
        </FormGroup>
      </GutterTooltipError>
    )
  }
)

interface ImageProgressIndicatorProps {
  loadingHammoqMessage: string
  loadingHammoqPercent: number
}

const imageProgressIndicatorStyles: UprightStyle = {
  parentDiv: {
    display: 'flex',
    alignItems: 'center'
  },
  childDiv: {
    width: '300px',
    marginLeft: 'auto',
    marginRight: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    rowGap: '8px'
  },
  flexBasis: { flexBasis: '100%' }
}

export const ImageProgressIndicator = React.memo<ImageProgressIndicatorProps>(
  ({ loadingHammoqMessage, loadingHammoqPercent }) => {
    return (
      <div className="dropzone-dashed-border" style={imageProgressIndicatorStyles.parentDiv}>
        <div style={imageProgressIndicatorStyles.childDiv}>
          <div style={imageProgressIndicatorStyles.flexBasis}>
            <ProgressBar intent="primary" value={loadingHammoqPercent / 100} />
          </div>
          <span>{loadingHammoqMessage}</span>
        </div>
      </div>
    )
  }
)
